import React, {
  Fragment, useState, useEffect, useRef
} from "react";
import PropTypes from "prop-types";
import classNames from 'classnames';

import Nav from "components/menus/Nav";
import NavLink from "components/menus/NavLink";
import Section from "components/Section";
import Fade from 'components/Fade';

// StyleGuide = organsims/menus/tabbed-sections
const getFilteredChildren = children => (Array.isArray(children) ? children.filter(Boolean) : children);

const getTabbedSectionClasses = styleVariant =>
  classNames('TabbedSections', 'd-flex', {
    'h-100': styleVariant !== 'QuestionModalTabs',
    'flex-column': styleVariant === 'default' || styleVariant === 'QuestionModalTabs' || styleVariant === "storefront",
    'flex-row': styleVariant === 'settings' || styleVariant === 'storefrontMobile'
  });

const getNavClasses = styleVariant =>
  classNames(`nav-${styleVariant}`, {
    'w-100': styleVariant === 'default' || styleVariant === 'QuestionModalTabs' || styleVariant === "storefront",
    'w-15': styleVariant === 'settings' || styleVariant === 'storefrontMobile',
    'd-block': styleVariant === 'settings' || styleVariant === 'storefrontMobile'
  });

const getTabContentClasses = styleVariant =>
  classNames('tab-content flex-grow-1 position-relative', {
    'w-100': styleVariant === 'default' || styleVariant === 'QuestionModalTabs' || styleVariant === "storefront",
    'w-85': styleVariant === 'settings' || styleVariant === 'storefrontMobile'
  });

const TabbedSections = ({
  children,
  className = "",
  activeTab = "",
  hideEmptyNav = false,
  disabled = [],
  styleVariant = 'default',
  highlightedSections = {},
  navHeader,
  callBack,
  customContent,
}) => {
  const elRef = useRef(null);
  const filteredChildren = getFilteredChildren(children);
  const childNames = filteredChildren.map(child => child.props.name);
  const childNamesKey = childNames.join('|');

  const [shouldShowTab, setShouldShowTab] = useState(
    activeTab || (filteredChildren.length > 0 && filteredChildren[0].props.name)
  );

  useEffect(() => {
    if (!childNames.length) return;

    if (activeTab && activeTab !== shouldShowTab && childNames.includes(activeTab)) {
      setShouldShowTab(activeTab);
      return;
    }

    if (!shouldShowTab || !childNames.includes(shouldShowTab)) {
      setShouldShowTab(activeTab && childNames.includes(activeTab) ? activeTab : childNames[0]);
    }
  }, [activeTab, childNamesKey]);

  useEffect(() => {
    if (callBack && shouldShowTab) callBack(shouldShowTab);
  }, [callBack, shouldShowTab]);

  const renderContentSection = (child) => {
    const thisTab = child.props.name;
    const contentClass = classNames([
      styleVariant === "storefrontMobile" ? "h-auto" : "h-100",
      { 'absolute-top w-100': shouldShowTab !== thisTab }
    ]);

    return (
      <Fade id={child.props.name} role="tabpanel" hide={shouldShowTab !== thisTab} className={contentClass} key={`${child.props.name}-tab`}>
        <Section
          key={child.props.name}
          className={child.props.className}
          styleVariant={styleVariant}
        >
          {child.props.children}
        </Section>
      </Fade>
    );
  };

  const renderNav = () => {
    if (hideEmptyNav && filteredChildren.length <= 1) return null;

    const navClasses = getNavClasses(styleVariant);

    return (
      <Nav role="tablist" navHeader={navHeader} styleVariant={styleVariant} className={`assignment_tabs ${navClasses}`}>
        {filteredChildren.map(child => (
          <Fragment key={child.props.name}>
            <NavLink
              role="tab"
              notificationQuantity={child.props.notificationQuantity}
              key={child.props.name}
              icon={child.props.icon}
              iconProps={child.props.iconProps}
              onClick={() => setShouldShowTab(child.props.name)}
              className={`${shouldShowTab === child.props.name ? "active" : ""} ${highlightedSections[child.props.name] ? "highlight" : ""} ${child.props.buttonClassName}`}
              disabled={disabled.includes(child.props.name)}
              styleVariant={styleVariant}
              active={shouldShowTab === child.props.name}
              ariaControls={child.props.name}
              tabIndex={child.props.tabIndex}
            >
              {child.props.name}
            </NavLink>
            {styleVariant === 'storefrontMobile' && renderContentSection(child)}
          </Fragment>
        ))}
        {customContent}
      </Nav>
    );
  };

  if (filteredChildren.length === 1) return filteredChildren[0];

  return (
    <div className={`${getTabbedSectionClasses(styleVariant)} ${className}`}>
      {renderNav()}
      {styleVariant !== 'storefrontMobile' && (
        <div className={getTabContentClasses(styleVariant)} ref={elRef}>
          {filteredChildren.map(child => renderContentSection(child))}
        </div>
      )}
    </div>
  );
};

TabbedSections.propTypes = {
  children: PropTypes.node.isRequired,
  styleVariant: PropTypes.oneOf([
    'settings',
    'default',
    'QuestionModalTabs',
    'storefront',
    'storefrontMobile'
  ]),
  navHeader: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element
  ]),
  className: PropTypes.string,
  activeTab: PropTypes.string,
  hideEmptyNav: PropTypes.bool,
  disabled: PropTypes.arrayOf(PropTypes.string),
  highlightedSections: PropTypes.object,
  callBack: PropTypes.func,
  customContent: PropTypes.node,
};

export default TabbedSections;
